<template>
  <RemotePaginatedList :hide-search="hideSearch"
                       :filter-options="filterOptions"
                       :excluded-filter-fields="excludeFilterFields"
                       :headers="headers"
                       multi-sort
                       :show-select="showSelect"
                       v-model="selectedItemIds"
                       data-url="sasv/admin/"
                       data-key="sasvs"
                       :refresh-trigger.sync="innerRefreshTrigger"
                       v-bind="$attrs">
    <template #item.update_at="{value}">
      {{ value ? $parseDate(value) : '' }}
    </template>
    <template #item.id_type="{value}">
      {{ $t(`id_type.${value}`) }}
    </template>
    <template #item.validated_at="{value}">
      {{ value ? $parseDate(value, 'yyyy-MM-dd') : '' }}
    </template>
    <template #item.status="{value}">
      {{ $t(`sasv_status.${value}`) }}
    </template>
    <template v-slot:item.actions="{item}">
      <v-container>
        <v-row>
          <v-col>
            <v-tooltip top
                       key="edit">
              <template #activator="{on, attrs}">
                <v-btn icon
                       x-small
                       v-on="on"
                       v-bind="attrs"
                       @click="editItem(item)">
                  <v-icon>fas fa-edit</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('edit') }}</span>
            </v-tooltip>
          </v-col>
          <v-col v-if="item.status === 'APPROVED' && $hasPermission('sasv.suspend')">
            <v-tooltip top
                       key="disable">
              <template #activator="{on, attrs}">
                <v-btn icon
                       x-small
                       v-on="on"
                       v-bind="attrs"
                       @click="suspendItem(item)">
                  <v-icon>fas fa-lock</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('admin_sasv.suspend') }}</span>
            </v-tooltip>
          </v-col>
          <v-col v-if="item.status === 'SUSPENDED' && $hasPermission('sasv.suspend')">
            <v-tooltip top
                       key="enable">
              <template #activator="{on, attrs}">
                <v-btn icon
                       x-small
                       v-on="on"
                       v-bind="attrs"
                       @click="unsuspendItem(item)">
                  <v-icon>fas fa-unlock</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('admin_sasv.unsuspend') }}</span>
            </v-tooltip>
          </v-col>
          <v-col v-if="item.status === 'PENDING_APPROVAL' || item.status === 'REJECTED'">
            <v-tooltip top
                       key="verify">
              <template #activator="{on, attrs}">
                <v-btn icon
                       x-small
                       v-on="on"
                       v-bind="attrs"
                       @click="startVerifyUser(item)">
                  <v-icon>fas fa-id-card</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('admin_user.validate_id_title') }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </RemotePaginatedList>
</template>

<script>
import {computed, onMounted, ref, watch} from '@vue/composition-api'
import RemotePaginatedList from "@/components/RemotePaginatedList";

export default {
  name: 'SasvList',
  components: {RemotePaginatedList},
  props: {
    hideSearch: Boolean,
    showSelect: Boolean,
    excludeFilterFields: Array,
    refreshTrigger: Boolean,
  },
  setup(props, {root, emit}) {
    const refresh = function () {
      innerRefreshTrigger.value = true;
      getCompanies();
    };
    onMounted(() => {
      refresh();
    });

    watch(() => props.refreshTrigger, (newValue) => {
      if (newValue) {
        refresh();
        emit('update:refresh-trigger', false);
      }
    })

    const companies = ref([]);
    const getCompanies = function () {
      root.$callAPI('hkiarace/admin/company/', {
        page_size: -1
      }, function (response) {
        companies.value = response.body.companies;
      });
    };

    // Filter
    const filterOptions = computed(() => {
      return [
        {text: root.$t('admin_sasv.octopus_no'), value: 'octopus_no', contains: true},
        {text: root.$t('admin_sasv.surname'), value: 'surname', contains: true},
        {text: root.$t('admin_sasv.given_name'), value: 'given_name', contains: true},
        {
          text: root.$t('admin_sasv.company'), value: 'company.id',
          options: companies.value.map((c) => {
            return {text: c.name, value: c.id};
          }),
        },
        {
          text: root.$t('admin_sasv.id_type'), value: 'id_type',
          options: [
            {text: root.$t('id_type.AIRPORT_OCTOPUS'), value: 'AIRPORT_OCTOPUS'},
            {text: root.$t('id_type.VIP'), value: 'VIP'},
            // {text: root.$t('id_type.PERSONAL_OCTOPUS_AND_STAFF_CARD'), value: 'PERSONAL_OCTOPUS_AND_STAFF_CARD'},
          ],
        },
        {
          text: root.$t('admin_sasv.status'), value: 'status',
          options: [
            {text: root.$t('sasv_status.NOT_SUBMITTED'), value: 'NOT_SUBMITTED'},
            {text: root.$t('sasv_status.PENDING_APPROVAL'), value: 'PENDING_APPROVAL'},
            {text: root.$t('sasv_status.APPROVED'), value: 'APPROVED'},
            {text: root.$t('sasv_status.REJECTED'), value: 'REJECTED'},
            {text: root.$t('sasv_status.SUSPENDED'), value: 'SUSPENDED'},
          ],
        },
        // {text: root.$t('admin_user.id_expiry_date'), value: 'id_expiry_date', date_range: true},
        // {text: root.$t('admin_user.last_login'), value: 'account.last_login', date_range: true},
      ]
    })

    // Data Table
    const headers = ref([
      {text: root.$t('admin_sasv.update_at'), value: 'update_at'},
      {text: root.$t('admin_sasv.octopus_no'), value: 'octopus_no'},
      {text: root.$t('admin_sasv.surname'), value: 'surname'},
      {text: root.$t('admin_sasv.given_name'), value: 'given_name'},
      {text: root.$t('admin_sasv.id_type'), value: 'id_type'},
      {text: root.$t('admin_sasv.company'), value: 'company.name'},
      {text: root.$t('admin_sasv.other_company_name'), value: 'other_company_name'},
      {text: root.$t('admin_sasv.status'), value: 'status'},
      {text: root.$t('admin_sasv.validated_at'), value: 'validated_at', boolean: false},

      {text: '', value: 'actions', sortable: false},
    ]);
    const innerRefreshTrigger = ref(false);

    const selectedItemIds = ref([]);
    watch(() => selectedItemIds.value, (newValue) => {
      emit('input', newValue);
    });

    const editItem = function (item) {
      emit('edit', item);
    };
    const suspendItem = function (item) {
      if (confirm(root.$t('admin_sasv.suspend_confirm_msg'))) {
        root.$callAPI('sasv/admin/suspend/', {id: item.id}, () => {
          refresh();
        })
      }
    };
    const unsuspendItem = function (item) {
      if (confirm(root.$t('admin_sasv.unsuspend_confirm_msg'))) {
        root.$callAPI('sasv/admin/unsuspend/', {id: item.id}, () => {
          refresh();
        })
      }
    };
    const startVerifyUser = function (item) {
      emit('verify', item);
    }


    return {
      filterOptions,

      headers,
      innerRefreshTrigger,
      selectedItemIds,

      editItem,
      suspendItem,
      unsuspendItem,
      startVerifyUser,
    }
  }
}
</script>
